import { FC, useEffect, useState } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { useTranslation } from '@hooks/useTranslation';
import { Organization } from '@models/organization.model';
import { OrganizationSubscriptionStatus } from '@models/subscription';
import SubscriptionStatusBadge from './SubscriptionStatusBadge';

const DEFAULT_EXTENSION_DAYS = 14;

const pad = (n: number) => String(n).padStart(2, '0');

/** YYYY-MM-DD from the LOCAL calendar parts of a date. */
export const toLocalDateInputValue = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Local calendar date `days` days from today. */
export const localDateFromToday = (days: number): string => {
  const now = new Date();
  return toLocalDateInputValue(
    new Date(now.getFullYear(), now.getMonth(), now.getDate() + days),
  );
};

/**
 * ISO instant for the end of the chosen local day. Built from local parts: the plain
 * `new Date('YYYY-MM-DD')` is UTC midnight and can land on the previous day.
 */
export const endOfLocalDayIso = (dateValue: string): string => {
  const [year, month, day] = dateValue.split('-').map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();
};

interface ExtendTrialModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization: Organization | null;
  /** Receives the ISO end-of-day instant. */
  onConfirm: (trialEndsAtIso: string) => void;
  isLoading: boolean;
}

const ExtendTrialModal: FC<ExtendTrialModalProps> = ({
  isOpen,
  onClose,
  organization,
  onConfirm,
  isLoading,
}) => {
  const t = useTranslation('organizations');
  const [date, setDate] = useState<string>(
    localDateFromToday(DEFAULT_EXTENSION_DAYS),
  );

  // Reset to today + 14 each time the modal opens.
  useEffect(() => {
    if (isOpen) setDate(localDateFromToday(DEFAULT_EXTENSION_DAYS));
  }, [isOpen, organization?.organizationId]);

  const minDate = localDateFromToday(1);
  const isInvalid = !date || date < minDate;
  const isNmi = !!organization?.hasNmiSubscription;
  const isFreeAdmin =
    organization?.subscriptionStatus === OrganizationSubscriptionStatus.ACTIVE &&
    !isNmi;

  const handleConfirm = () => {
    if (isInvalid || isNmi) return;
    onConfirm(endOfLocalDayIso(date));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      handleConfirm={handleConfirm}
      title={t.extendFreePeriod}
      confirmText={t.extendFreePeriod}
      disableConfirm={isInvalid || isNmi}>
      <div className="flex flex-col gap-6">
        {organization && (
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-2">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t.organization}
            </p>
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              {organization.name}
            </p>
            <div className="flex flex-col gap-1">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {t.currentStatus}
              </span>
              <SubscriptionStatusBadge
                status={organization.subscriptionStatus}
                trialEndsAt={organization.trialEndsAt}
                trialExpiredAt={organization.trialExpiredAt}
              />
            </div>
            {isFreeAdmin && (
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {t.extendFreeAdminNote}
              </p>
            )}
          </div>
        )}

        <FormGroup
          htmlFor="trialEndsAt"
          label={t.extendDateLabel}
          error={!isNmi && isInvalid ? t.extendInvalidDate : undefined}>
          <Input
            id="trialEndsAt"
            type="date"
            value={date}
            min={minDate}
            disabled={isNmi || isLoading}
            onChange={(event) => setDate(event.target.value)}
          />
        </FormGroup>

        {isNmi ? (
          <div
            role="alert"
            className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg text-sm text-amber-800 dark:text-amber-200">
            {t.extendNmiReason}
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t.extendHelper}
            </p>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg text-sm text-blue-800 dark:text-blue-200">
              {t.extendInfo}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ExtendTrialModal;
